import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import { SCard } from "@/components/Card/SCard";
import { SError } from "@/components/Error/SError";
import { Spacing } from "@/components/Spacing/Spacing";
import { UserSidebar } from "@/components/UserSidebar/UserSidebar";
import { warmCommonUserRoutes } from "@/components/UserSidebar/routePrefetch";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { getCompanyRole } from "@/lib/api/handlers/CompanyRoleHandling";
import { getCompany } from "@/lib/api/handlers/CompanyHandling";
import type { CompanyRole } from "@/types/CompanyRole";

function isServerErr(x: unknown): x is { isServerError: true; message: string } {
  return (
    !!x &&
    typeof x === "object" &&
    "isServerError" in x &&
    (x as { isServerError?: boolean }).isServerError === true
  );
}

export function UserLayoutShell() {
  const t = useTranslations("userSettings");

  useEffect(() => {
    type WindowWithIdle = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as WindowWithIdle;
    if (!w.requestIdleCallback) return;
    const id = w.requestIdleCallback(() => warmCommonUserRoutes(), { timeout: 2000 });
    return () => w.cancelIdleCallback?.(id);
  }, []);

  const roleQ = useQuery({
    queryKey: ["userLayout", "companyRole"],
    queryFn: () => getCompanyRole("role"),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const companyQ = useQuery({
    queryKey: ["userLayout", "company", "customerType"],
    queryFn: () => getCompany("customerType"),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Fatal-error gate only — fires once roleQ resolves with a server error.
  // Loading state intentionally falls through so the sidebar can paint
  // immediately; only the inner Outlet waits via <Suspense>.
  if (roleQ.isError || isServerErr(roleQ.data)) {
    const msg = isServerErr(roleQ.data)
      ? roleQ.data.message
      : roleQ.error instanceof Error
        ? roleQ.error.message
        : "unknown";
    return <SError error={msg} />;
  }

  const role: string | null =
    roleQ.data && !isServerErr(roleQ.data)
      ? ((roleQ.data as CompanyRole).role ?? null)
      : null;
  const customerType =
    companyQ.data && !isServerErr(companyQ.data)
      ? ((companyQ.data as { customerType?: string }).customerType ?? "INDIVIDUAL")
      : "INDIVIDUAL";

  return (
    <div className="flex flex-col flex-1 w-full p-4 sm:p-6">
      <h1 className="h2 w-full mx-3 sm:mx-6 max-w-300">{t("userSettings")}</h1>
      <Spacing height="h-6" />
      <SCard
        width="w-full max-w-300"
        height="flex-1 "
        padding="p-3 md:p-6"
        marginOuter="mx-3 sm:mx-6"
        shadow="shadow-md"
        justify="flex flex-col items-center"
        rounded="rounded-2xl"
        outline="outline-quinary"
      >
        <div className="flex flex-col md:flex-row w-full h-full flex-1">
          <UserSidebar role={role} customerType={customerType} />
          <div
            data-testid="page-root"
            // min-w-0: without it this flex item's automatic min-width is its
            // content's min-content size, so a wide child (the CRM contacts
            // Table sets an explicit px width) expands the row past the
            // SCard's max-w-300 instead of scrolling inside its own
            // overflow-x container. min-w-0 only permits shrinking — narrow
            // /user pages are unaffected.
            className="border-t md:border-t-0 md:border-l outline-tertiary flex-1 min-w-0"
          >
            <Suspense fallback={<InlineLoadingSpinner />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </SCard>
    </div>
  );
}
